import {useEffect, useState} from "react";

import {useViewer} from "../context/ViewerContext";
import {evaluateFlag} from "../utils/flags";

/**
 * LaunchDarkly JSON flag driving the revamped profile listing in the main app
 * (see ProfileListingVirtualizedConfigSchema in @buildwithflux/app-flags-schema).
 * Its `searchCardUnification` control gates the unified search cards here.
 */
const FLAG_KEY = "profile-listing-virtualized";

function isUnificationEnabled(value: unknown): boolean {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as {searchCardUnification?: unknown}).searchCardUnification === true
    );
}

/**
 * Whether the unified shared project cards (@buildwithflux/project-cards) are
 * enabled for this viewer. Defaults to false (the legacy search card): SSR and
 * the first client render always use the legacy card, and the flag is
 * evaluated client-side once auth has resolved.
 */
export function useSearchCardUnification(): boolean {
    const viewer = useViewer();
    const {uid, email, handle, authResolved} = viewer;
    const identityKey = `${uid ?? ""}\0${email ?? ""}\0${handle ?? ""}`;
    const [enabled, setEnabled] = useState(false);
    const [lastIdentityKey, setLastIdentityKey] = useState(identityKey);
    if (identityKey !== lastIdentityKey) {
        setLastIdentityKey(identityKey);
        setEnabled(false);
    }

    useEffect(() => {
        if (!authResolved) {
            return undefined;
        }
        let cancelled = false;
        void evaluateFlag(FLAG_KEY, viewer)
            .then((value) => {
                if (!cancelled) {
                    setEnabled(isUnificationEnabled(value));
                }
            })
            .catch(() => {
                // Keep the legacy rendering when evaluation fails
            });
        return () => {
            cancelled = true;
        };
    }, [authResolved, viewer]);

    return enabled;
}
